/**
 * useCart Hook
 * Manages shopping cart state and operations
 * 
 * Responsibilities:
 * - Cart items management (add, remove, update quantity)
 * - Cart total calculation
 * - Cart persistence (localStorage + Firebase sync)
 * - Inventory availability check
 * - Toast notifications for cart actions
 * - Checkout modal state
 * 
 * Storage Pattern:
 * - Guest: localStorage only (heseda-ai_cart)
 * - Logged in: localStorage + Firebase sync (masterProfiles/{uid}/cart)
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { checkAvailability } from '../services/inventoryService';
import type { CartItem } from '../types';

interface UseCartProps {
  language?: string;
  userId?: string | null;
}

interface UseCartReturn {
  // Cart state
  cartItems: CartItem[];
  setCartItems: React.Dispatch<React.SetStateAction<CartItem[]>>;
  cartTotal: number;
  cartItemCount: number;
  
  // UI state
  showCart: boolean;
  setShowCart: (show: boolean) => void;
  toastMessage: string | null;
  setToastMessage: React.Dispatch<React.SetStateAction<string | null>>;
  
  // Cart operations
  handleAddToCart: (product: CartItem) => Promise<void>;
  handleUpdateCartQuantity: (productId: string, quantity: number) => void;
  handleRemoveFromCart: (productId: string) => void;
  clearCart: () => void;
  
  // Sync status
  isSyncing: boolean;
}

const CART_STORAGE_KEY = 'heseda-ai_cart';

// Migrate legacy localStorage key
try {
  const old = localStorage.getItem('skinmatch_cart');
  if (old && !localStorage.getItem(CART_STORAGE_KEY)) {
    localStorage.setItem(CART_STORAGE_KEY, old);
    localStorage.removeItem('skinmatch_cart');
  }
} catch { /* ignore */ }

/**
 * Custom hook for managing shopping cart functionality
 * Syncs with Firebase when user is logged in
 */
export function useCart({ language = 'ko', userId }: UseCartProps = {}): UseCartReturn {
  // Cart items state with localStorage persistence
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.warn('[useCart] Failed to load cart from localStorage:', error);
      return [];
    }
  });
  
  // UI state
  const [showCart, setShowCart] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Track if we've loaded from Firebase this session
  const hasLoadedFromFirebase = useRef(false);
  const previousUserId = useRef<string | null | undefined>(undefined);
  
  // Track current userId for saveToFirebase closure
  const currentUserIdRef = useRef<string | null | undefined>(userId);
  currentUserIdRef.current = userId;
  
  // Calculate cart total
  const cartTotal = cartItems.reduce((total, item) => {
    const price = item.priceUSD || item.price || 0;
    const quantity = item.quantity || 1;
    return total + (price * quantity);
  }, 0);

  // Calculate cart item count
  const cartItemCount = cartItems.reduce((count, item) => count + (item.quantity || 1), 0);
  
  // Persist cart to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.warn('[useCart] Failed to save cart to localStorage:', error);
    }
  }, [cartItems]);

  /**
   * Reset refs and optionally clear cart when user logs out
   */
  useEffect(() => {
    if (!userId && previousUserId.current) {
      // User just logged out - reset sync tracking
      console.log('[useCart] User logged out, resetting sync state');
      hasLoadedFromFirebase.current = false;
      previousUserId.current = undefined;
      // Clear cart on logout to prevent data leaking
      setCartItems([]);
      try {
        localStorage.removeItem(CART_STORAGE_KEY);
      } catch (e) {
        console.warn('[useCart] Failed to clear localStorage on logout:', e);
      }
    }
  }, [userId]);

  /**
   * Sync cart with Firebase when user logs in
   * Uses Firebase as source of truth, merges with any local items
   */
  useEffect(() => {
    // No user - nothing to sync
    if (!userId) {
      return;
    }
    
    // Skip if we already loaded for this user
    if (previousUserId.current === userId && hasLoadedFromFirebase.current) {
      return;
    }
    
    const syncWithFirebase = async () => {
      setIsSyncing(true);
      console.log('[useCart] Starting Firebase sync for user:', userId);
      
      try {
        const profileRef = doc(db, 'masterProfiles', userId);
        const profileSnap = await getDoc(profileRef);
        
        if (profileSnap.exists()) {
          const data = profileSnap.data();
          const firebaseCart = (data.cart || []) as CartItem[];
          
          // Capture local cart state at sync start (closure captures this at effect creation time)
          // This represents items added as guest before login
          const localCartAtSyncStart = cartItems;
          
          // Merge: Firebase items + local items not already in Firebase
          const firebaseIds = new Set(firebaseCart.map(item => item.id));
          const newLocalItems = localCartAtSyncStart.filter(item => !firebaseIds.has(item.id));
          
          let finalCart: CartItem[];
          
          if (newLocalItems.length > 0) {
            // Have new local items - merge and save back
            finalCart = [...firebaseCart, ...newLocalItems];
            await updateDoc(profileRef, { cart: finalCart });
            console.log('[useCart] Merged local items with Firebase cart');
          } else {
            // No new local items - use Firebase cart as source of truth
            finalCart = firebaseCart;
          }
          
          setCartItems(finalCart);
          console.log('[useCart] Loaded cart from Firebase:', finalCart.length, 'items');
        } else if (cartItems.length > 0) {
          // No Firebase profile yet, save local cart
          await setDoc(profileRef, { cart: cartItems }, { merge: true });
          console.log('[useCart] Saved local cart to new Firebase profile');
        } else {
          // No Firebase profile and no local items - ensure cart is empty
          setCartItems([]);
        }
        
        hasLoadedFromFirebase.current = true;
        previousUserId.current = userId;
      } catch (error) {
        console.warn('[useCart] Firebase sync failed:', error);
        // On error, still mark as loaded to prevent infinite retries
        hasLoadedFromFirebase.current = true;
        previousUserId.current = userId;
      } finally {
        setIsSyncing(false);
      }
    };
    
    syncWithFirebase();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Only depend on userId, not cartItems to avoid loops

  /**
   * Save cart to Firebase (non-blocking)
   * Uses currentUserIdRef to get latest userId in async callbacks
   */
  const saveToFirebase = useCallback(async (items: CartItem[]) => {
    const uid = currentUserIdRef.current;
    if (!uid) {
      console.log('[useCart] No user, skipping Firebase save');
      return;
    }
    
    console.log('[useCart] Saving cart to Firebase:', items.length, 'items');
    
    try {
      const profileRef = doc(db, 'masterProfiles', uid);
      await updateDoc(profileRef, { cart: items });
      console.log('[useCart] Cart saved to Firebase successfully');
    } catch (error) {
      // If document doesn't exist, create it with merge
      try {
        const profileRef = doc(db, 'masterProfiles', uid);
        await setDoc(profileRef, { cart: items }, { merge: true });
        console.log('[useCart] Cart saved to new Firebase profile');
      } catch (setError) {
        console.warn('[useCart] Failed to save cart to Firebase:', setError);
      }
    }
  }, []); // No dependencies - uses ref for userId

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);
  
  /**
   * Add a product to the cart with inventory check
   * If product already exists, increment quantity
   */
  const handleAddToCart = useCallback(async (product: CartItem) => {
    // Check inventory availability
    try {
      const availability = await checkAvailability([{ ...product, quantity: 1 }]);
      
      if (!availability.available) {
        setToastMessage(language === 'en' 
          ? `❌ ${product.name} is out of stock!` 
          : `❌ ${product.name}의 재고가 없습니다!`);
        return;
      }
    } catch (error) {
      console.warn('Could not check inventory:', error);
      // Continue with add to cart even if check fails
    }

    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      let newItems: CartItem[];
      
      if (existingItem) {
        setToastMessage(language === 'en' 
          ? `${product.name} quantity increased!` 
          : `${product.name} 수량이 증가했습니다!`);
        newItems = prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        setToastMessage(language === 'en' 
          ? `${product.name} added to cart!` 
          : `${product.name}이(가) 장바구니에 추가되었습니다!`);
        newItems = [...prevItems, { ...product, quantity: 1 }];
      }
      
      // Save to Firebase async (non-blocking)
      saveToFirebase(newItems);
      return newItems;
    });
    setShowCart(true);
  }, [language, saveToFirebase]);
  
  /**
   * Remove a product from the cart
   */
  const handleRemoveFromCart = useCallback((productId: string) => {
    setCartItems(prevItems => {
      const newItems = prevItems.filter(item => item.id !== productId);
      // Save to Firebase async (non-blocking)
      saveToFirebase(newItems);
      return newItems;
    });
  }, [saveToFirebase]);
  
  /**
   * Update the quantity of a cart item
   * If quantity is 0 or less, remove the item
   */
  const handleUpdateCartQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveFromCart(productId);
      return;
    }
    
    setCartItems(prevItems => {
      const newItems = prevItems.map(item => 
        item.id === productId 
          ? { ...item, quantity } 
          : item
      );
      // Save to Firebase async (non-blocking)
      saveToFirebase(newItems);
      return newItems;
    });
  }, [handleRemoveFromCart, saveToFirebase]);
  
  /**
   * Clear all items from the cart
   */
  const clearCart = useCallback(() => {
    setCartItems([]);
    saveToFirebase([]);
  }, [saveToFirebase]);
  
  return {
    // State
    cartItems,
    setCartItems,
    cartTotal,
    cartItemCount,
    
    // UI state
    showCart,
    setShowCart,
    toastMessage,
    setToastMessage,
    
    // Operations
    handleAddToCart,
    handleRemoveFromCart,
    handleUpdateCartQuantity,
    clearCart,
    
    // Sync status
    isSyncing,
  };
}

export default useCart;
